import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Mic, MicOff } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { useHubToast } from "@/components/ui/HubToast";
import {
  appendSpeechTranscript,
  chromeSpeechRecognitionSupported,
  createChromeSpeechRecognition,
} from "@/lib/chromeSpeechRecognition";
import { cn } from "@/lib/utils";

export function VoiceToTextMicButton({
  value,
  onValueChange,
  disabled = false,
  className,
  ariaLabel = "Dictate with microphone",
}: {
  value: string;
  onValueChange: (next: string) => void;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}): JSX.Element | null {
  const { showToast } = useHubToast();
  const supported = chromeSpeechRecognitionSupported();
  const recognitionRef = useRef<ReturnType<typeof createChromeSpeechRecognition>>(null);
  const valueRef = useRef(value);
  const [listening, setListening] = useState(false);
  const [starting, setStarting] = useState(false);

  valueRef.current = value;

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setListening(false);
    setStarting(false);
  }, []);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  const startListening = useCallback(() => {
    if (disabled || !supported) return;
    const recognition = createChromeSpeechRecognition();
    if (recognition == null) {
      showToast({ message: "Speech recognition is not available in this browser.", tone: "error" });
      return;
    }

    recognitionRef.current = recognition;
    setStarting(true);

    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result?.isFinal) {
          finalTranscript += result[0]?.transcript ?? "";
        }
      }
      if (finalTranscript.trim() === "") return;
      onValueChange(appendSpeechTranscript(valueRef.current, finalTranscript));
    };

    recognition.onerror = (event) => {
      if (event.error === "aborted" || event.error === "no-speech") {
        stopListening();
        return;
      }
      stopListening();
      showToast({
        message:
          event.error === "not-allowed"
            ? "Microphone access was blocked. Allow the mic for this site in Chrome."
            : `Speech recognition failed: ${event.error}`,
        tone: "error",
      });
    };

    recognition.onend = () => {
      if (recognitionRef.current === recognition) {
        stopListening();
      }
    };

    try {
      recognition.start();
      setListening(true);
    } catch {
      stopListening();
      showToast({ message: "Could not start speech recognition.", tone: "error" });
    } finally {
      setStarting(false);
    }
  }, [disabled, onValueChange, showToast, stopListening, supported]);

  const toggleListening = useCallback(() => {
    if (listening || starting) {
      stopListening();
      return;
    }
    startListening();
  }, [listening, startListening, starting, stopListening]);

  if (!supported) return null;

  return (
    <Button
      type="button"
      variant="secondary"
      className={cn(
        "h-9 w-9 shrink-0 p-0",
        listening ? "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100" : "",
        className,
      )}
      disabled={disabled || starting}
      aria-label={listening ? "Stop dictation" : ariaLabel}
      title={listening ? "Stop dictation" : ariaLabel}
      aria-pressed={listening}
      onClick={toggleListening}
    >
      {starting ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : listening ? (
        <MicOff className="h-4 w-4" aria-hidden />
      ) : (
        <Mic className="h-4 w-4" aria-hidden />
      )}
    </Button>
  );
}
